import { useState, useEffect, useCallback } from "react";
import { getFriendlyErrorMessage } from "../utils/errorMapper";
import type { Plan } from "@/mocks/plans";
import { plans as fallbackPlans } from "@/mocks/plans";

const API_BASE = "https://wpp.debugin.com/ai";

interface ApiPlan {
  key: string;
  name: string;
  duration: number;
  price: number;
  features: {
    messages: number;
    tokens: number;
  };
}

interface ApiResponse {
  success: boolean;
  message?: string;
  data?: {
    plans: ApiPlan[];
  };
}

const EXTRA_FEATURES_MAP: Record<string, Plan["extraFeatures"]> = {
  basic: [
    { key: "plans.feature.textResponse", included: true },
    { key: "plans.feature.voiceResponse", included: true },
    { key: "plans.feature.imageResponse", included: false },
    { key: "plans.feature.documentResponse", included: false },
  ],
  pro: [
    { key: "plans.feature.textResponse", included: true },
    { key: "plans.feature.voiceResponse", included: true },
    { key: "plans.feature.imageResponse", included: true },
    { key: "plans.feature.documentResponse", included: false },
  ],
  enterprise: [
    { key: "plans.feature.textResponse", included: true },
    { key: "plans.feature.voiceResponse", included: true },
    { key: "plans.feature.imageResponse", included: true },
    { key: "plans.feature.documentResponse", included: true },
  ],
};

export function formatPlanPrice(amount: number, lang: string): string {
  const formatted = new Intl.NumberFormat("en-US").format(amount);
  if (lang === "ar") {
    return `${formatted} ج.م`;
  }
  return `EGP ${formatted}`;
}

function mapApiPlan(apiPlan: ApiPlan): Plan {
  return {
    key: apiPlan.key,
    name: `plans.name.${apiPlan.key}`,
    duration: apiPlan.duration,
    features: {
      messages: apiPlan.features.messages,
      tokens: apiPlan.features.tokens,
    },
    description: `plans.description.${apiPlan.key}`,
    highlighted: apiPlan.key === "pro",
    price: apiPlan.price.toString(),
    amount: apiPlan.price,
    extraFeatures: EXTRA_FEATURES_MAP[apiPlan.key] || [],
  };
}

export function usePlans() {
  const [plans, setPlans] = useState<Plan[]>(fallbackPlans);
  const [loading, setLoading] = useState(true);
  const [error, setErrorState] = useState<string | null>(null);
  const setError = useCallback((err: string | null) => {
    setErrorState(err ? getFriendlyErrorMessage(err) : null);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function fetchPlans() {
      try {
        const res = await fetch(`${API_BASE}/plans`);

        if (!res.ok) {
          throw new Error(`Server returned ${res.status}`);
        }

        const json: ApiResponse = await res.json();

        if (!json.success || !json.data?.plans?.length) {
          throw new Error(json.message || "No plans returned from API");
        }

        const mapped = json.data.plans.map(mapApiPlan);

        if (!cancelled) {
          setPlans(mapped);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : "Failed to load plans";
          setError(message);
          setPlans(fallbackPlans);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchPlans();

    return () => {
      cancelled = true;
    };
  }, []);

  return { plans, loading, error };
}